// 1. React imports
import React, { useState } from 'react'

// 2. External library imports
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { AppBar, Toolbar, Typography, Button, Box, Menu, MenuItem, Tab, Tabs, Avatar, IconButton } from '@mui/material'
import {
  Logout,
  Business,
  Warehouse,
  Inventory,
  LocalShipping,
  DeliveryDining,
  DashboardCustomizeSharp,
  TabletAndroidRounded,
  Forklift,
  Menu as MenuIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material'

// 3. Internal imports
import { useAuth } from '@/hooks/useAuth'
import { zIndexes } from '@/utils/zIndexes'

interface TopNavProps {
  onMenuClick: () => void
  sidePanelOpen: boolean
}

// Navigation items configuration - matching workstreams from menuItems
const apexClientNavigationItems = [
  { label: 'BackOffice', icon: Business, workstream: 'backoffice' },
  { label: 'Linehaul', icon: SwapHorizIcon, workstream: 'linehaul' },
  { label: 'Inbound', icon: Warehouse, workstream: 'inbound' },
  { label: 'Outbound', icon: Inventory, workstream: 'outbound' },
  { label: 'Dispatch Web', icon: LocalShipping, workstream: 'dispatch' },
  { label: 'My Daylight', icon: DeliveryDining, workstream: 'my-daylight' },
]

// CloudWatch navigation items configuration
const cloudWatchNavigationItems = [
  { label: 'BackOffice', icon: Business, workstream: 'backoffice' },
  { label: 'Linehaul', icon: SwapHorizIcon, workstream: 'linehaul' },
  { label: 'Inbound', icon: Warehouse, workstream: 'inbound' },
  { label: 'Outbound', icon: Inventory, workstream: 'outbound' },
  { label: 'Dispatch Web', icon: LocalShipping, workstream: 'dispatch' },
  { label: 'Dispatch Mobile', icon: TabletAndroidRounded, workstream: 'dispatch-mobile' },
  { label: 'Dock Automation', icon: Forklift, workstream: 'dock-automation' },
]

// Overview navigation items configuration
const overviewNavigationItems = [
  {
    label: 'Apex Client',
    icon: DashboardCustomizeSharp,
    workstream: 'apex-client',
  },
  {
    label: 'Dispatch Mobile',
    icon: TabletAndroidRounded,
    workstream: 'dispatch-mobile',
  },
  { label: 'Dock Automation', icon: Forklift, workstream: 'dock-automation' },
]

export default function TopNav({ onMenuClick, sidePanelOpen }: TopNavProps) {
  const { user, signOutUser } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  // Determine if Apex Client is selected (apex-client route)
  const isApexClientsSelected = location.pathname === '/apex-client'
  const isDispatchMobile = location.pathname === '/dispatch-mobile'
  const isOverviewSelected = location.pathname === '/'
  const isCloudWatchSelected = location.pathname === '/cloud-watch'

  // Show workstream tabs (BackOffice, Linehaul, etc.) only on Apex Client page
  const showApexWorkstreamTabs = isApexClientsSelected && !isDispatchMobile

  // Show workstream tabs for CloudWatch page
  const showCloudWatchWorkstreamTabs = isCloudWatchSelected

  // Determine active tab for Apex Client workstreams based on query param
  // Default to 'backoffice' if no workstream is specified
  const currentWorkstream = searchParams.get('workstream') || 'backoffice'
  const activeApexTab = isApexClientsSelected
    ? apexClientNavigationItems.findIndex((item) => item.workstream === currentWorkstream)
    : -1

  // Determine active tab for CloudWatch workstreams based on query param
  // Default to 'backoffice' if no workstream is specified
  const currentCloudWatchWorkstream = searchParams.get('workstream') || 'backoffice'
  const activeCloudWatchTab = isCloudWatchSelected
    ? cloudWatchNavigationItems.findIndex((item) => item.workstream === currentCloudWatchWorkstream)
    : -1

  // Determine active tab for Overview based on dashboard query param
  // Default to 'apex-client' if no dashboard is specified
  const currentDashboard = searchParams.get('dashboard') || 'apex-client'
  const activeOverviewTab = isOverviewSelected
    ? overviewNavigationItems.findIndex((item) => item.workstream === currentDashboard)
    : -1

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSignOut = async () => {
    try {
      await signOutUser()
      navigate('/login')
    } catch {
      // Silent fail
    }
    handleClose()
  }

  const handleTabChangeApexClient = (_event: React.SyntheticEvent, newValue: number) => {
    if (apexClientNavigationItems[newValue]) {
      const workstream = apexClientNavigationItems[newValue].workstream
      // Navigate to apex-client with workstream query param
      if (location.pathname === '/apex-client') {
        // Update query params if already on apex-client
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set('workstream', workstream)
        navigate(`/apex-client?${newSearchParams.toString()}`, {
          replace: true,
        })
      } else {
        // Navigate to apex-client with workstream
        navigate(`/apex-client?workstream=${workstream}`)
      }
    }
  }

  const handleTabChangeOverview = (_event: React.SyntheticEvent, newValue: number) => {
    if (overviewNavigationItems[newValue]) {
      const dashboard = overviewNavigationItems[newValue].workstream
      // Stay on overview page, just change the dashboard query param
      navigate(`/?dashboard=${dashboard}`, { replace: true })
    }
  }

  const handleTabChangeCloudWatch = (_event: React.SyntheticEvent, newValue: number) => {
    if (cloudWatchNavigationItems[newValue]) {
      const workstream = cloudWatchNavigationItems[newValue].workstream
      // Navigate to cloud-watch with workstream query param
      if (location.pathname === '/cloud-watch') {
        // Update query params if already on cloud-watch
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set('workstream', workstream)
        navigate(`/cloud-watch?${newSearchParams.toString()}`, {
          replace: true,
        })
      } else {
        // Navigate to cloud-watch with workstream
        navigate(`/cloud-watch?workstream=${workstream}`)
      }
    }
  }

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: '#FFFFFF',
        boxShadow: 'none',
        borderBottom: '1px solid #DDDDDD',
        zIndex: zIndexes.topNav,
        top: 0,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          px: 2.5,
          minHeight: '64px !important',
          py: 0,
        }}
      >
        {/* Menu Button and Navigation Tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 1 }}>
          {/* Menu Button - Only show when side panel is closed */}
          {!sidePanelOpen && (
            <IconButton
              onClick={onMenuClick}
              sx={{
                color: '#616161',
                padding: '10px',
                '&:hover': {
                  backgroundColor: '#EEEEEE',
                },
              }}
            >
              <MenuIcon sx={{ fontSize: 22 }} />
            </IconButton>
          )}

          {/* Navigation Tabs with Icons - Only show when overview is selected */}
          {isOverviewSelected && (
            <Tabs
              value={activeOverviewTab !== -1 ? activeOverviewTab : false}
              onChange={handleTabChangeOverview}
              sx={{
                minHeight: '84px',
                '& .MuiTab-root': {
                  minHeight: '84px',
                  minWidth: '160px',
                  textTransform: 'none',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#616161',
                  padding: '10px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',

                  '&.Mui-selected': {
                    color: '#026AB7',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#026AB7',
                  height: 2.5,
                },
              }}
            >
              {overviewNavigationItems.map((item) => {
                const IconComponent = item.icon
                return (
                  <Tab
                    key={item.workstream}
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <IconComponent sx={{ fontSize: 24 }} />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 'inherit',
                            lineHeight: 1.2,
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    }
                  />
                )
              })}
            </Tabs>
          )}

          {/* Navigation Tabs with Icons - Only show on apex-client */}
          {showApexWorkstreamTabs && (
            <Tabs
              value={activeApexTab !== -1 ? activeApexTab : false}
              onChange={handleTabChangeApexClient}
              sx={{
                minHeight: '84px',
                '& .MuiTab-root': {
                  minHeight: '84px',
                  minWidth: '134px',
                  textTransform: 'none',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#616161',
                  padding: '10px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',

                  '&.Mui-selected': {
                    color: '#026AB7',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#026AB7',
                  height: 2.5,
                },
              }}
            >
              {apexClientNavigationItems.map((item) => {
                const IconComponent = item.icon
                return (
                  <Tab
                    key={item.workstream}
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <IconComponent sx={{ fontSize: 24 }} />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 'inherit',
                            lineHeight: 1.2,
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    }
                  />
                )
              })}
            </Tabs>
          )}

          {/* Navigation Tabs with Icons - Only show on cloud-watch */}
          {showCloudWatchWorkstreamTabs && (
            <Tabs
              value={activeCloudWatchTab !== -1 ? activeCloudWatchTab : false}
              onChange={handleTabChangeCloudWatch}
              sx={{
                minHeight: '84px',
                '& .MuiTab-root': {
                  minHeight: '84px',
                  minWidth: '134px',
                  textTransform: 'none',
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#616161',
                  padding: '10px 14px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',

                  '&.Mui-selected': {
                    color: '#026AB7',
                    fontWeight: 600,
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#026AB7',
                  height: 2.5,
                },
              }}
            >
              {cloudWatchNavigationItems.map((item) => {
                const IconComponent = item.icon
                return (
                  <Tab
                    key={item.workstream}
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '2px',
                        }}
                      >
                        <IconComponent sx={{ fontSize: 24 }} />
                        <Typography
                          sx={{
                            fontSize: '14px',
                            fontWeight: 'inherit',
                            lineHeight: 1.2,
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Box>
                    }
                  />
                )
              })}
            </Tabs>
          )}
        </Box>

        {/* Right Side - Notifications and User Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* User Profile */}
          <Button
            onClick={handleClick}
            sx={{
              backgroundColor: 'transparent',
              border: '1px solid #E0E0E0',
              borderRadius: '4px',
              padding: '6px 10px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#EEEEEE',
              },
            }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                backgroundColor: '#026AB7',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {user?.email?.charAt(0).toUpperCase() || 'M'}
            </Avatar>
            <Typography
              sx={{
                color: '#424242',
                fontWeight: 600,
                fontSize: '16px',
                fontStyle: 'bold',
                lineHeight: '143%',
                letterSpacing: '0.4px',
              }}
            >
              {user?.email?.split('@')[0] || 'Michael'}
            </Typography>
          </Button>

          {/* User Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              mt: 1,
              '& .MuiPaper-root': {
                borderRadius: '8px',
                minWidth: 180,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            {user?.email && (
              <MenuItem
                disabled
                sx={{
                  '&.Mui-disabled': {
                    opacity: 1,
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontSize: '12px', color: '#757575' }}>
                  {user.email}
                </Typography>
              </MenuItem>
            )}
            <MenuItem
              onClick={handleSignOut}
              sx={{
                fontSize: '13px',
                py: 1.25,
                '&:hover': {
                  backgroundColor: '#F5F5F5',
                },
              }}
            >
              <Logout sx={{ mr: 1.5, fontSize: 18, color: '#616161' }} />
              Sign Out
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  )
}
